<template>
  <div class="list-stat-card" :class="cardClasses" @click="handleClick">
    <!-- Header with Title -->
    <div class="card-header">
      <h3 class="card-title">{{ config.title }}</h3>
      <span v-if="config.badge" :class="badgeClasses">{{ config.badge }}</span>
    </div>

    <!-- Main Content -->
    <div class="card-content">
      <!-- Primary metric if available -->
      <div v-if="config.showTotal" class="total-section mb-4">
        <div class="total-value text-2xl font-bold text-gray-900">
          {{ formatValue(getTotalValue()) }}
        </div>
        <div class="total-label text-sm text-gray-600">
          {{ config.totalLabel || "Total" }}
        </div>
      </div>

      <!-- List Items -->
      <div class="list-items space-y-3">
        <div
          v-for="(item, index) in listItems"
          :key="`list-item-${index}`"
          class="list-item"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <!-- Icon if provided -->
              <div
                v-if="item.icon"
                class="item-icon mr-3"
                :class="getIconClasses(item.color)"
              >
                <i class="material-icons text-sm">{{ item.icon }}</i>
              </div>

              <!-- Color indicator if no icon -->
              <div
                v-else-if="item.color"
                class="w-3 h-3 rounded-full mr-3"
                :class="getColorClass(item.color)"
              ></div>

              <!-- Label -->
              <span class="item-label text-sm text-gray-700">
                {{ item.label }}
              </span>
            </div>

            <!-- Value -->
            <div class="item-value">
              <span class="text-sm font-semibold text-gray-900">
                {{ formatValue(getItemValue(item)) }}
              </span>
              <span v-if="item.unit" class="text-xs text-gray-500 ml-1">
                {{ item.unit }}
              </span>
            </div>
          </div>

          <!-- Progress bar if enabled -->
          <div
            v-if="config.showProgress && item.showProgress !== false"
            class="progress-section mt-2"
          >
            <div class="progress-bar bg-gray-200 rounded-full h-1.5">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="getProgressColorClass(item.color)"
                :style="{ width: getProgressWidth(item) + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer actions if provided -->
      <div
        v-if="config.actions"
        class="card-actions mt-4 pt-3 border-t border-gray-200"
      >
        <button
          v-for="(action, index) in config.actions"
          :key="`action-${index}`"
          class="action-btn"
          :class="getActionClasses(action)"
          @click.stop="handleAction(action)"
        >
          <i v-if="action.icon" class="material-icons text-sm mr-1">{{
            action.icon
          }}</i>
          {{ action.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ListStatCard",
  props: {
    config: {
      type: Object,
      required: true
    },
    data: {
      type: [Object, Array],
      default: () => ({})
    }
  },
  computed: {
    cardClasses() {
      return [
        "bg-white rounded-lg shadow-sm border border-gray-200 p-6",
        "hover:shadow-md transition-shadow duration-200 cursor-pointer",
        this.config.className || ""
      ];
    },

    badgeClasses() {
      const badgeColor = this.config.badgeColor || "blue";
      return [
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
        this.getBadgeColorClasses(badgeColor)
      ];
    },

    listItems() {
      if (this.config.items) {
        return this.config.items;
      }

      // Auto-generate items from data if items not explicitly provided
      if (Array.isArray(this.data)) {
        return this.data;
      }

      // Generate from object keys
      return Object.keys(this.data).map(key => ({
        key,
        label: this.formatLabel(key),
        value: this.data[key]
      }));
    }
  },
  methods: {
    formatValue(value) {
      if (typeof value === "number") {
        return value.toLocaleString();
      }
      return value || "0";
    },

    formatLabel(key) {
      // Convert camelCase to Title Case
      return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, str => str.toUpperCase());
    },

    getTotalValue() {
      if (this.config.totalKey) {
        return this.data[this.config.totalKey] || 0;
      }

      // Calculate total from items
      return this.listItems.reduce((total, item) => {
        const value = this.getItemValue(item);
        return total + (typeof value === "number" ? value : 0);
      }, 0);
    },

    getItemValue(item) {
      if (item.value !== undefined) {
        return item.value;
      }
      if (item.key && this.data[item.key] !== undefined) {
        return this.data[item.key];
      }
      return 0;
    },

    getIconClasses(color) {
      const colorMap = {
        blue: "text-blue-600 bg-blue-100",
        green: "text-green-600 bg-green-100",
        red: "text-red-600 bg-red-100",
        yellow: "text-yellow-600 bg-yellow-100",
        purple: "text-purple-600 bg-purple-100",
        gray: "text-gray-600 bg-gray-100"
      };
      return [
        "w-6 h-6 rounded-full flex items-center justify-center",
        colorMap[color] || colorMap.gray
      ];
    },

    getColorClass(color) {
      const colorMap = {
        blue: "bg-blue-500",
        green: "bg-green-500",
        red: "bg-red-500",
        yellow: "bg-yellow-500",
        purple: "bg-purple-500",
        gray: "bg-gray-500"
      };
      return colorMap[color] || colorMap.gray;
    },

    getProgressColorClass(color) {
      const colorMap = {
        blue: "bg-blue-500",
        green: "bg-green-500",
        red: "bg-red-500",
        yellow: "bg-yellow-500",
        purple: "bg-purple-500",
        gray: "bg-gray-500"
      };
      return colorMap[color] || colorMap.blue;
    },

    getProgressWidth(item) {
      const value = this.getItemValue(item);
      const max = item.max || this.config.maxValue || 100;
      return Math.min((value / max) * 100, 100);
    },

    getBadgeColorClasses(color) {
      const colorMap = {
        blue: "bg-blue-100 text-blue-800",
        green: "bg-green-100 text-green-800",
        red: "bg-red-100 text-red-800",
        yellow: "bg-yellow-100 text-yellow-800",
        purple: "bg-purple-100 text-purple-800",
        gray: "bg-gray-100 text-gray-800"
      };
      return colorMap[color] || colorMap.blue;
    },

    getActionClasses(action) {
      return [
        "text-sm px-3 py-1 rounded-md transition-colors duration-200",
        action.primary
          ? "bg-blue-600 text-white hover:bg-blue-700"
          : "text-blue-600 hover:bg-blue-50"
      ];
    },

    handleClick() {
      this.$emit("card-click", {
        type: "list",
        config: this.config,
        data: this.data
      });
    },

    handleAction(action) {
      this.$emit("action-click", {
        action: action.key || action.label,
        config: this.config,
        data: this.data
      });
    }
  }
};
</script>

<style scoped>
.list-stat-card {
  min-height: 200px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.card-content {
  flex: 1;
}

.list-item {
  transition: all 0.2s ease;
}

.list-item:hover {
  background-color: #f9fafb;
  border-radius: 0.375rem;
  padding: 0.5rem;
  margin: -0.5rem;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.action-btn:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Animation for progress bars */
.progress-bar .h-full {
  transition: width 0.5s ease-in-out;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .list-item:hover {
    margin: 0;
    padding: 0.25rem;
  }
}
</style>
